/**
 * Estado do tempo do loop — CONTRATO COMPARTILHADO.
 *
 * Dono: módulo app-core (goes/loop). Outros módulos (focos, exportação) só
 * LEEM `frames`, `index`, `currentTime`, `range` e `displayedTime`, e podem
 * chamar `setIndex`.
 * O dono pode acrescentar campos/ações, mas não deve renomear os existentes.
 */
import { create } from 'zustand';

export interface TimeRange {
  /** Início da janela do loop (epoch ms, UTC). */
  start: number;
  /** Fim da janela do loop (epoch ms, UTC). */
  end: number;
}

export interface TimeState {
  /** Instantes (epoch ms, UTC) dos quadros do loop, em ordem crescente. */
  frames: number[];
  /** Índice do quadro atual em `frames` (0 quando vazio). */
  index: number;
  /** Instante do quadro atual (epoch ms, UTC), ou null se não há quadros. */
  currentTime: number | null;
  /** Janela temporal do loop, ou null antes do primeiro carregamento. */
  range: TimeRange | null;
  /**
   * Instante da imagem GOES que está DE FATO na tela (a da camada de base).
   * Difere de `currentTime` só por instantes — enquanto o quadro pedido ainda
   * está sendo decodificado, a imagem anterior continua visível. Os focos
   * seguem este valor, para nunca aparecerem fora de sincronia com a imagem.
   * Null = nenhuma imagem na tela (siga `currentTime`).
   */
  displayedTime: number | null;

  setFrames: (frames: number[], range: TimeRange | null) => void;
  setIndex: (index: number) => void;
  /** (app-core) Troca quadros, janela e índice numa única atualização. */
  replaceFrames: (frames: number[], range: TimeRange | null, index: number) => void;
  /** (app-core) Publica o instante da imagem exibida (ver `displayedTime`). */
  setDisplayedTime: (t: number | null) => void;
}

/** Instante que as camadas sincronizadas com a imagem devem usar. */
export function shownTimeOf(s: Pick<TimeState, 'displayedTime' | 'currentTime'>): number | null {
  return s.displayedTime ?? s.currentTime;
}

export const useTimeStore = create<TimeState>()((set, get) => ({
  frames: [],
  index: 0,
  currentTime: null,
  range: null,
  displayedTime: null,

  setDisplayedTime: (t) => {
    if (get().displayedTime !== t) set({ displayedTime: t });
  },

  setFrames: (frames, range) => {
    const index = frames.length === 0 ? 0 : Math.min(get().index, frames.length - 1);
    set({ frames, range, index, currentTime: frames.length ? frames[index] : null });
  },

  setIndex: (index) => {
    const { frames } = get();
    if (frames.length === 0) return;
    const i = ((index % frames.length) + frames.length) % frames.length;
    set({ index: i, currentTime: frames[i] });
  },

  replaceFrames: (frames, range, index) => {
    const i = frames.length === 0 ? 0 : Math.max(0, Math.min(index, frames.length - 1));
    set({ frames, range, index: i, currentTime: frames.length ? frames[i] : null });
  },
}));
